import {
    HttpErrorResponse,
    HttpInterceptorFn,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApiConnectorConfiguration } from '@wasmachen/api-connector';
import {
    Observable,
    catchError,
    filter,
    finalize,
    map,
    shareReplay,
    switchMap,
    take,
    throwError,
} from 'rxjs';
import {
    TwoFactorPromptData,
    TwoFactorPromptDialogComponent,
} from './two-factor-prompt.dialog';

/** One prompt for all the requests that hit the requirement at once (a page loads several). */
let pendingPrompt: Observable<boolean> | null = null;

/**
 * Organizations that require 2FA answer Manager+ ticketing calls with 403 `TWO_FACTOR_REQUIRED`
 * until a code was entered in this sign-in. This asks for the code, then retries the request;
 * cancelling passes the original error on.
 */
export const twoFactorInterceptor: HttpInterceptorFn = (req, next) => {
    const dialog = inject(MatDialog);
    const apiConfig = inject(ApiConnectorConfiguration);

    return next(req).pipe(
        catchError((err) => {
            if (
                !(err instanceof HttpErrorResponse) ||
                err.status !== 403 ||
                err.error?.code !== 'TWO_FACTOR_REQUIRED'
            ) {
                return throwError(() => err);
            }
            if (!pendingPrompt) {
                const data: TwoFactorPromptData = {
                    enrolled: !!err.error?.enrolled,
                    verify: (code) =>
                        next(
                            new HttpRequest(
                                'POST',
                                `${apiConfig.basePath}/api/two-factor/verify`,
                                { code },
                                { withCredentials: req.withCredentials }
                            )
                        ).pipe(
                            filter((e) => e instanceof HttpResponse),
                            take(1)
                        ),
                };
                pendingPrompt = dialog
                    .open(TwoFactorPromptDialogComponent, {
                        data,
                        width: '420px',
                    })
                    .afterClosed()
                    .pipe(
                        map((ok) => ok === true),
                        finalize(() => (pendingPrompt = null)),
                        shareReplay(1)
                    );
            }
            return pendingPrompt.pipe(
                switchMap((ok) => (ok ? next(req) : throwError(() => err)))
            );
        })
    );
};
